import CapacityRequestDrawer from './CapacityRequestDrawer';
import { DensityProvider, useDensity } from '../../../contexts/DensityContext';
import { CapacityValidationContext, CapacityValidationControl } from './CapacityValidation';
import CustomerLookupField from '../../order-management/components/CustomerLookupField';
import { useCapacityInputClass } from './useCapacityInputClass';
import Toggle from '../../../components/Toggle';
import MarketConditionsImageThumb from '../../../components/MarketConditionsImageThumb';
import MarketConditionsImageViewer from '../../../components/MarketConditionsImageViewer';
import { useMarketConditions } from '../../../services/marketConditionsStore';
import { prefetchImages } from '../../../services/marketConditionsImageCache';
import { marketConditionDot, marketConditionNote, marketConditionTone, marketStatusLabel } from '../../../utils/marketConditionStatus';
import { CapacityDateInput, Select } from './CapacityInputs';
export { Select } from './CapacityInputs';
import CapacityVetting from './CapacityVetting';
import { evaluateCapacityRules } from './rules';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Drawer } from '../../../components/ui/Drawer';
import { addRequests, loadOrderContext, refreshCapacity, applySharedDate, blankLoad, blankRequest, isOpen, isRequestForPro, markets, origins, preOrderReasons, reasons, useCapacityPreview, type CapacityRequest, type OrderContext } from './model';
export const buttonClass = 'rounded-md border border-theme-border-primary bg-panel px-3 py-1.5 text-sm font-medium text-body hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40';
export const primaryClass = 'rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40';
function marketFromPro(value: string) {
  return markets.find(market => market.toLowerCase() === value.trim().toLowerCase()) ?? '';
}

export function PreviewNotice() {
  const { loading, error } = useCapacityPreview();
  if (loading) return <p role="status" className="mb-4 text-sm text-muted">Loading capacity requests…</p>;
  return error ? <div role="alert" className="mb-4 rounded border border-red-400 p-3 text-sm text-red-600">{error} <button onClick={() => void refreshCapacity()} className="underline">Retry</button></div> : null;
}
export function Field({
  label,
  children,
  required = false,
  validationKey = label
}: {
  label: string;
  children: ReactNode;
  required?: boolean;
  validationKey?: string;
}) {
  const compact = useDensity();
  return <label className="block"><span className={`block font-medium text-label ${compact ? 'mb-1 text-xs' : 'mb-2 text-sm'}`}>{label}{required && <span aria-hidden="true" className="text-red-600"> *</span>}</span><CapacityValidationControl name={validationKey}>{children}</CapacityValidationControl></label>;
}
function FormSection({ title, description, children }: { title: string; description?: string; children: ReactNode }) {
  return <section aria-label={title} className="my-3 rounded-lg border border-theme-border-primary bg-panel p-3">
    <h3 className="mb-2 text-sm font-semibold text-heading">{title}</h3>
    {description && <p className="mb-3 text-xs text-muted">{description}</p>}
    <div className="grid gap-x-3 gap-y-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, max(220px, calc((100% - 36px) / 4))), 1fr))' }}>{children}</div>
  </section>;
}

/** Screenshots shown per page in the conditions panel. */
const PER_PAGE = 3;
export function MarketConditionsPanel({
  market,
  // The default stacks it above the next block; a host laying it out beside
  // something else passes its own sizing instead.
  className = 'mb-4'
}: {
  market: string;
  className?: string;
}) {
  // Market conditions come from their own store, not the capacity snapshot, so
  // this page shows exactly what the header pill shows -- one source, one set
  // of grants. A user who cannot read them sees nothing rather than stale text.
  const { notes, loaded, failed } = useMarketConditions();
  const compact = useDensity();
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [carousel, setCarousel] = useState(0);
  const note = notes[market];
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date());
  const current = note?.conditionDay === today;
  const status = (current ? note.conditionStatus : undefined) ?? 'green';
  const daysOut = current ? note?.daysOut : null;
  const images = note?.images ?? [];
  // Clamp rather than reset on change: the set shrinks when a screenshot is
  // removed, and the market can change without this unmounting.
  const pageCount = Math.max(1, Math.ceil(images.length / PER_PAGE));
  const page = Math.min(carousel, pageCount - 1);
  const setSlide = (next: number) => { setCarousel(next); setViewerIndex(null); };
  // A market carries a handful of screenshots, so warm them all once rather
  // than fetching three at a time as the user pages.
  const imageIds = images.map(image => image.id).join(',');
  useEffect(() => { if (imageIds) prefetchImages(imageIds.split(',').map(Number)); }, [imageIds]);
  // Always state the market's standing, including "Normal Operations" -- absence
  // of a comment is itself the answer. The exception is a read that never
  // succeeded, where green would assert the opposite of "unknown".
  if (!loaded || (failed && !Object.keys(notes).length)) return null;
  // Same tone, dot and days-out treatment as the header pill, so a market reads
  // the same whichever surface it is seen on.
  return <aside className={`rounded-lg border ${compact ? 'mb-3 p-2' : 'mb-4 p-4'} ${marketConditionTone[status]}`}>
    <div className="flex items-start justify-between gap-2">
      <h3 className={compact ? "text-sm font-semibold" : "font-semibold"}>Market conditions · {market}{daysOut != null && <span className="ml-2 whitespace-nowrap text-xs font-medium opacity-80">· {daysOut} days out</span>}</h3>
      <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${marketConditionDot[status]}`} role="img" aria-label={`${marketStatusLabel(status)} status`} title={marketStatusLabel(status)} />
    </div>
    <p className={`whitespace-pre-wrap break-words ${compact ? 'mt-1 text-xs' : 'mt-2 text-sm'}`}>{marketConditionNote(note?.text)}</p>
    {/* Three small tiles at a time: a grid of four wrapped onto a second row
        and pushed the page down. Each button owns its click so it is keyboard
        reachable; the thumbnail must not also open a tab of its own. */}
    {images.length > 0 && <div className="mt-3">
      <div className="grid grid-cols-3 gap-1.5">
        {images.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE).map((image, offset) => {
          const index = page * PER_PAGE + offset;
          return <button key={image.id} type="button" className="rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label={`Open ${image.name} full size`} onClick={() => setViewerIndex(index)}>
            <MarketConditionsImageThumb id={image.id} name={image.name} interactive={false} heightClass="h-16" />
          </button>;
        })}
      </div>
      {pageCount > 1 && <div className="mt-1 flex items-center justify-between text-xs opacity-80">
        <button type="button" aria-label="Previous screenshots" className="rounded px-2 py-0.5 hover:bg-black/10"
          onClick={() => setSlide((page - 1 + pageCount) % pageCount)}>‹</button>
        <span>{images.length} screenshots</span>
        <button type="button" aria-label="More screenshots" className="rounded px-2 py-0.5 hover:bg-black/10"
          onClick={() => setSlide((page + 1) % pageCount)}>›</button>
      </div>}
    </div>}
    {/* The 10px used on the pill's narrow cards is too small here, where the
        panel runs the full width of the page. */}
    {current && note?.author && <p className="mt-3 text-xs opacity-75">Updated by {note.author.split('@')[0]} · {new Date(note.updatedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</p>}
    {viewerIndex !== null && <MarketConditionsImageViewer market={market} images={images} index={viewerIndex}
      onIndexChange={setViewerIndex} onClose={() => setViewerIndex(null)} />}
  </aside>;
}
interface RequestFormProps {
  orders?: OrderContext[];
  onClose: () => void;
}

export function RequestForm(props: RequestFormProps) {
  return <DensityProvider compact><CapacityRequestForm {...props} /></DensityProvider>;
}

function CapacityRequestForm({ orders, onClose }: RequestFormProps) {
  const {
    requests, managers
  } = useCapacityPreview();
  const inputClass = useCapacityInputClass();
  const [requestGrouping, setRequestGrouping] = useState('One shared request');
  const [manualCustomer, setManualCustomer] = useState(false);
  const [customerCode, setCustomerCode] = useState(0);
  const [form, setForm] = useState<CapacityRequest>(() => ({
    ...blankRequest(),
    ...(orders?.[0] ? {
      source: 'PRO',
      customer: orders[0].customer,
      market: marketFromPro(orders[0].market),
      move: orders[0].move || 'IMPORT',
      lfd: orders[0].lfd || '',
      erd: orders[0].erd || '',
      cutoff: orders[0].cutoff || '',
      miles: orders[0].miles || '',
      keyTier: orders[0].keyTier || '',
      pool: orders[0].pool || 'Local'
    } : {})
  }));
  const [rows, setRows] = useState(() => (orders ?? []).map(o => ({
    ...o,
    date: '',
    touched: false,
    selected: !o.appointment && !requests.some(r => isRequestForPro(r, o.pro) && isOpen(r))
  })));
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [contextLoading, setContextLoading] = useState(!!orders);
  const [contextFailures, setContextFailures] = useState<Record<string, string>>({});
  const [contextAttempt, setContextAttempt] = useState(0);
  const orderIds = orders?.map(order => order.pro).join(',') ?? '';
  useEffect(() => {
    if (!orderIds) { setContextLoading(false); return; }
    let active = true;
    setContextLoading(true);
    setContextFailures({});
    const pros = orderIds.split(',');
    Promise.allSettled(pros.map(loadOrderContext)).then(results => {
      if (!active) return;
      const contexts: OrderContext[] = [];
      const failures: Record<string, string> = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') contexts.push(result.value);
        else {
          const detail = (result.reason as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
          failures[pros[index]] = typeof detail === 'string' ? detail : 'Could not load current PRO details.';
        }
      });
      setRows(current => current.map(row => ({ ...row, ...contexts.find(context => context.pro === row.pro) })));
      setContextFailures(failures);
      setContextLoading(false);
    });
    return () => { active = false; };
  }, [orderIds, contextAttempt]);
  const [deadline, setDeadline] = useState('No deadline');
  const [error, setError] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [duplicateConfirmed, setDuplicateConfirmed] = useState(false);
  const isPro = !!orders;
  const [manager, setManager] = useState('');
  const [justification, setJustification] = useState('');
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(timer);
  }, []);
  const checkRows = (at: number) => rows.filter(r => r.selected).map(r => ({
    pro: r.pro,
    rules: evaluateCapacityRules({ ...form, keyTier: r.keyTier || '', availableAt: r.availableAt, date: r.date, lfd: r.move === 'EXPORT' || r.move === 'DOMESTIC' ? '' : r.lfd || '' }, at),
  }));
  const ruleChecks = checkRows(now);
  const includedPros = rows.filter(row => row.selected);
  const failedIncludedPros = includedPros.filter(row => contextFailures[row.pro]);
  const confirmedPros = includedPros.filter(row => !contextFailures[row.pro]);
  const selectedMarkets = [...new Set(confirmedPros.map(row => marketFromPro(row.market)).filter(Boolean))];
  const selectedPools = [...new Set(confirmedPros.map(row => row.pool || 'Local'))];
  const unroutedPros = includedPros.filter(row => !marketFromPro(row.market));
  const showImportDates = form.for !== 'Bobtail' && rows.some(row => row.move !== 'EXPORT' && row.move !== 'DOMESTIC');
  const showExportDates = form.for !== 'Bobtail' && rows.some(row => row.move === 'EXPORT');

  const validationErrors: Record<string, string> = {};
  const required = (label: string, value: string | undefined, message = 'This field is required.') => {
    if (!value?.trim()) validationErrors[label] = message;
  };
  const validateMiles = (label: string, value: string | undefined) => {
    if (!value?.trim() || !Number.isFinite(Number(value)) || Number(value) <= 0)
      validationErrors[label] = 'Enter mileage greater than zero.';
  };
  if (isPro && requestGrouping === 'One shared request' && selectedMarkets.length > 1)
    validationErrors['Create as'] = 'Shared requests require PROs in the same market. Choose separate requests.';
  if (isPro && unroutedPros.length) validationErrors['Market (from PROs)'] = `No capacity market is configured for PRO ${unroutedPros.map(row => row.pro).join(', ')}. Check the dispatcher routing shown below with scheduling, or exclude these PROs.`;
  if (!isPro) required('Market', form.market);
  if (!isPro) required('Customer / prospect', manualCustomer ? form.customer : customerCode ? form.customer : '',
    'Select a customer, or choose Add As New Customer.');
  required('What is the customer asking for?', form.ask);
  if (isPro) {
    if (!rows.some(row => row.selected)) validationErrors['PROs'] = 'Select at least one eligible PRO.';
    rows.filter(row => row.selected).forEach(row => {
      if (!form.asap) required(`Requested date for ${row.pro}`, row.date);
      validateMiles(`Mileage for ${row.pro}`, row.miles);
    });
  } else if (form.for === 'Bobtail') {
    if (!form.asap) required('Requested date', form.date);
    validateMiles('Approximate miles', form.miles);
  } else {
    form.loads.forEach((load, index) => {
      required(`${index}:Destination`, load.destination);
      if (!form.asap) required(`${index}:Requested date`, load.date);
      validateMiles(`${index}:Approximate miles`, load.miles);
    });
  }
  if (ruleChecks.some(check => check.rules.needsVetting)) {
    if (!managers.includes(manager)) validationErrors['Vetted by (GWI360-LCManager)'] = 'Select the CS manager who vetted this request.';
    if (justification.trim().length < 10) validationErrors['Why this is outside normal parameters'] = 'Enter at least 10 characters.';
  }
  useLayoutEffect(() => {
    if (!validationAttempt) return;
    const first = contentRef.current?.querySelector<HTMLElement>('[data-capacity-invalid="true"]');
    const input = first?.querySelector<HTMLElement>('input:not([type="hidden"]):not(:disabled), textarea:not(:disabled), select:not(:disabled), output');
    first?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    (input ?? first)?.focus({ preventScroll: true });
  }, [validationAttempt]);

  const patch = <K extends keyof CapacityRequest,>(key: K, value: CapacityRequest[K]) => setForm(f => ({
    ...f,
    [key]: value
  }));
  const field = (label: string, key: 'customer' | 'date' | 'lfd' | 'erd' | 'cutoff' | 'miles' | 'margin' | 'cadence' | 'quote' | 'quoteRate' | 'examLfd' | 'examSite' | 'examOffBy' | 'windowStart' | 'windowEnd', type = 'text') => <Field label={label} required={key === 'customer' || key === 'miles'}>{type === 'date' || type === 'time' ? <CapacityDateInput mode={type} ariaLabel={label} value={form[key]} onChange={value => patch(key, value)} /> : <input aria-label={label} required={key === 'customer' || key === 'miles'} min={key === 'miles' ? 0 : undefined} step={key === 'miles' ? 'any' : undefined} className={inputClass} type={type} value={form[key]} onChange={e => patch(key, e.target.value)} />}</Field>;
  const submit = async () => {
    if (busy || contextLoading || failedIncludedPros.length) return;
    setNow(Date.now());
    setValidationAttempt(attempt => attempt + 1);
    if (Object.keys(validationErrors).length) {
      const messages = Object.values(validationErrors);
      setError(`Complete the highlighted required fields. ${[...new Set(messages)].join(' ')}`);
      return;
    }
    const checks = checkRows(Date.now());
    const duplicates = requests.filter(r => r.source === 'PRE-ORDER' && isOpen(r) && r.customer.trim().toLowerCase() === form.customer.trim().toLowerCase() && r.market === form.market);
    if (!isPro && duplicates.length && !duplicateConfirmed) {
      setDuplicateConfirmed(true);
      setError(`Already open: ${duplicates.map(r => `#${r.id} · ${r.status} · ${r.requester}`).join('; ')}. Send again to continue.`);
      return;
    }
    let dueAt: number | undefined;
    if (deadline === 'End of day') {
      const end = new Date();
      end.setHours(17, 0, 0, 0);
      dueAt = end.getTime();
    } else if (deadline !== 'No deadline') dueAt = Date.now() + Number(deadline) * 60000;
    const base = {
      ...form,
      dueAt,
      date: form.asap ? '' : form.date,
      exam: form.for !== 'Bobtail' && form.exam,
      examLfd: form.for !== 'Bobtail' && form.exam ? form.examLfd : '',
      examSite: form.for !== 'Bobtail' && form.exam ? form.examSite : '',
      examOffBy: form.for !== 'Bobtail' && form.exam ? form.examOffBy : '',
      windowStart: form.appointment === 'Hard appointment' ? form.windowStart : '',
      windowEnd: form.appointment === 'Hard appointment' ? form.windowEnd : '',
      thread: []
    };
    const batch = rows.filter(r => r.selected).length > 1 ? `batch-${Date.now()}` : undefined;
    setBusy(true);
    try {
    await addRequests(isPro ? rows.filter(r => r.selected).map(r => ({
      ...base,
      pro: r.pro,
      handling: r.handling || '',
      order: r,
      ...(checks.find(c => c.pro === r.pro)?.rules.needsVetting ? {
        escalation: { manager, justification: justification.trim(), reasons: [
          ...(checks.find(c => c.pro === r.pro)?.rules.pushBlocked ? ['Push lock'] : []),
          ...(checks.find(c => c.pro === r.pro)?.rules.lfdBlocked ? ['Same-day LFD'] : []),
          ...(checks.find(c => c.pro === r.pro)?.rules.keyCritical ? ['Key account Critical'] : []),
        ] },
        thread: [...base.thread, `ESCALATION — Vetted by ${manager}: ${justification.trim()}. Vetting recorded.`],
      } : {}),
      customer: r.customer,
      market: marketFromPro(r.market),
      move: r.move || form.move,
      lfd: r.move === 'EXPORT' || r.move === 'DOMESTIC' ? '' : r.lfd || '',
      erd: r.move === 'EXPORT' ? r.erd || '' : '',
      cutoff: r.move === 'EXPORT' ? r.cutoff || '' : '',
      miles: r.miles || '',
      keyTier: r.keyTier || '',
      pool: r.pool || form.pool,
      date: form.asap ? '' : r.date,
      loads: [],
      batch
    })) : [{
      ...base,
      date: form.asap ? '' : form.for === 'Bobtail' ? base.date : form.loads[0]?.date || '',
      loads: form.for === 'Bobtail' ? [] : form.loads.map(l => ({ ...l, date: form.asap ? '' : l.date, lfd: form.move === 'IMPORT' ? l.lfd : '', erd: form.move === 'EXPORT' ? l.erd : '', cutoff: form.move === 'EXPORT' ? l.cutoff : '' }))
    }], files, isPro && requestGrouping === 'One shared request' && rows.filter(row => row.selected).length > 1);
    onClose();
    } catch (e) { setError(e instanceof Error ? e.message : "Could not save request."); } finally { setBusy(false); }
  };
  const footer = <div className="flex justify-end gap-2">
    <button className={buttonClass} onClick={onClose}>Cancel</button>
    <button className={primaryClass} disabled={busy || contextLoading || failedIncludedPros.length > 0} onClick={submit}>{busy ? 'Saving…' : contextLoading ? 'Loading PRO…' : 'Send request'}</button>
  </div>;
  const content = <CapacityValidationContext.Provider value={validationAttempt ? validationErrors : {}}><div ref={contentRef} className="text-sm">
    <PreviewNotice />
    {Object.keys(contextFailures).length > 0 && <section role="alert" className="mb-4 space-y-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
      <p className="font-semibold">Some PRO details could not be loaded.</p>
      <ul>{Object.entries(contextFailures).map(([pro, message]) => <li key={pro}>{message.includes(pro) ? message : `PRO ${pro}: ${message}`}</li>)}</ul>
      <p>Retry, or turn off Include for these PROs to continue with the others.</p>
      <button type="button" className={buttonClass} disabled={busy || contextLoading} onClick={() => setContextAttempt(attempt => attempt + 1)}>Retry PRO details</button>
    </section>}
    <p className="mb-2 text-xs text-muted">Fields marked * are required.</p>
    {(isPro ? contextLoading ? [] : selectedMarkets : [form.market]).map(m => <MarketConditionsPanel key={m} market={m} />)}
    <FormSection title="Shipment details">
      {isPro && orders.length > 1 && <div><Select label="Create as" value={requestGrouping} options={['One shared request', 'Separate request per PRO']} onChange={setRequestGrouping} /><p className="mt-1 text-xs text-muted">{requestGrouping === 'One shared request' ? 'One conversation and decision for the selected PROs.' : 'Each selected PRO gets its own request and decision.'}</p></div>}
      {!isPro && <><div>
        {manualCustomer ? <>
          {field('Customer / prospect', 'customer')}
          <button type="button" disabled={busy} className="mt-2 text-xs font-medium text-blue-600 underline hover:text-blue-700 dark:text-blue-400"
            onClick={() => { setManualCustomer(false); setCustomerCode(0); setError(''); }}>
            Search existing customers
          </button>
          <p className="mt-1 text-xs text-muted">For a customer or prospect not yet in the system.</p>
        </> : <Field label="Customer / prospect" required><CustomerLookupField
          ariaLabel="Customer / prospect" value={form.customer} linkedCode={customerCode}
          onType={name => { setCustomerCode(0); patch('customer', name); }}
          onPick={customer => { setCustomerCode(customer.customercode); patch('customer', customer.custname); }}
          onAddNew={name => { setManualCustomer(true); setCustomerCode(0); patch('customer', name); setError(''); }}
        /></Field>}
      </div><Select required label="Market" value={form.market} options={markets} onChange={v => {
          patch('market', v);
          patch('loads', form.loads.map(l => ({
            ...l,
            origin: ''
          })));
          setDuplicateConfirmed(false);
        }} /><Select required label="Move" value={form.move} options={['IMPORT', 'EXPORT', 'DOMESTIC']} onChange={v => {
          patch('move', v);
          patch('loads', form.loads.map(l => ({
            ...l,
            origin: ''
          })));
        }} /></>}
      {isPro && <div><Field label="Market (from PROs)"><output aria-label="Market (from PROs)" tabIndex={-1} className="block min-h-8 py-1 text-sm text-heading">{contextLoading ? 'Loading PRO details…' : failedIncludedPros.length ? 'Some PRO details unavailable' : includedPros.length ? selectedMarkets.join(' / ') || 'Unavailable' : 'No PROs included'}</output></Field></div>}
      <Select required label="Request for" value={form.for} options={['Container move', 'Bobtail']} onChange={v => patch('for', v)} />
      {isPro ? <div><Field label="Local / OTR"><output aria-label="Local / OTR" tabIndex={-1} className="block min-h-8 py-1 text-sm text-heading">{contextLoading ? 'Loading PRO details…' : failedIncludedPros.length ? 'Some PRO details unavailable' : selectedPools.join(' / ') || '—'}</output></Field></div> : <Select required label="Local / OTR" value={form.pool} options={['Local', 'OTR']} onChange={v => patch('pool', v)} />}
    </FormSection>
    <FormSection title="Scheduling" description={form.type === 'Capacity' ? 'Fit an unscheduled leg into capacity.' : form.type === 'Push' ? 'Move a scheduled leg earlier.' : 'Set a new date after a missed or changed schedule.'}>
      <Select required label="Request type" value={form.type} options={['Capacity', 'Push', 'Reschedule']} onChange={v => patch('type', v)} />
      <Select required label="Leg" value={form.leg} options={['Prepull', 'Delivery', 'Both']} onChange={v => patch('leg', v)} />
      <Select required label="Urgency" value={form.urgency} options={['Routine', 'Hot', 'Critical']} onChange={v => patch('urgency', v)} />
      <Select label="Answer needed by" value={deadline} onChange={setDeadline} options={['No deadline', ...[30, 60, 120, 240].map(n => ({ value: String(n), label: n < 60 ? '30 minutes' : `${n / 60} hour${n > 60 ? 's' : ''}` })), 'End of day']} />
      {!isPro && form.for === 'Bobtail' && <Field label="Requested date" required={!form.asap}><CapacityDateInput ariaLabel="Requested date" disabled={form.asap} value={form.date} onChange={value => {
          patch('date', value);
          setRows(r => applySharedDate(r, value));
          patch('loads', applySharedDate(form.loads, value));
        }} /></Field>}
      {!isPro && form.for === 'Bobtail' && <label className="flex items-center gap-2 self-end py-2 text-xs text-body"><Toggle size="xs" ariaLabel="ASAP / on arrival" checked={form.asap} onChange={checked => patch('asap', checked)} />ASAP / on arrival</label>}
      {!isPro && <Select label="Live unload / Drop" value={form.handling || ''} options={['', 'Live unload', 'Drop']} onChange={value => patch('handling', value as CapacityRequest['handling'])} />}
      <Select required label="Appointment" value={form.appointment} options={['Open receiving', 'Hard appointment']} onChange={v => patch('appointment', v)} />
      {form.appointment === 'Hard appointment' && <>{field('Window start', 'windowStart', 'time')}{field('Window end', 'windowEnd', 'time')}</>}
      {form.for === 'Bobtail' && <Field label="Unit count" required><input aria-label="Unit count" required type="number" min={1} className={inputClass} value={form.units} onChange={e => patch('units', Math.max(1, Number(e.target.value)))} /></Field>}
      {!isPro && form.for === 'Bobtail' && field('Approximate miles', 'miles', 'number')}
    </FormSection>
    <FormSection title="Additional details">
      {form.for !== 'Bobtail' && <Select label="Exam Move" value={form.exam ? 'Yes' : 'No'} options={['No', 'Yes']} onChange={value => patch('exam', value === 'Yes')} />}
      {form.exam && form.for !== 'Bobtail' && <>{field('Exam last free day', 'examLfd', 'date')}{field('Exam site / stop-off', 'examSite')}{field('Off exam site by', 'examOffBy', 'date')}</>}
      {isPro && <>{field('Quote number', 'quote')}{field('Quote rate', 'quoteRate')}</>}
      {!isPro && <>{field('Margin', 'margin')}<Select label="Cadence" value={form.cadence} options={['', 'One off', 'Per week', 'Per day']} onChange={v => patch('cadence', v)} /></>}
    </FormSection>
    {isPro && <CapacityValidationControl name="PROs"><section className="my-3 space-y-2" aria-labelledby="capacity-pros-heading">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div><h3 id="capacity-pros-heading" className="text-sm font-semibold text-heading">PROs in this request</h3><p className="mt-1 text-xs text-muted">Turn on Include for each PRO to send. Provide mileage, then set a service date or choose ASAP.</p></div>
        <span className="text-xs font-medium text-body">{rows.filter(row => row.selected).length} of {rows.length} included</span>
      </div>
      <div className="rounded-lg border border-theme-border-primary bg-panel p-2">
        {rows.length > 1 && <p className="mb-1 text-xs font-medium text-label">Set requested date for all PROs</p>}
        <div className="flex flex-wrap items-center gap-3">
          {rows.length > 1 && <div className="w-full max-w-xs"><CapacityDateInput ariaLabel="Set requested date for all PROs" disabled={form.asap} value={form.date} onChange={value => {
            patch('date', value);
            setRows(current => applySharedDate(current, value));
          }} /></div>}
          <label className="flex items-center gap-2 py-1 text-xs text-body"><Toggle size="xs" ariaLabel="ASAP / on arrival" checked={form.asap} onChange={checked => patch('asap', checked)} />ASAP / on arrival</label>
        </div>
        {rows.length > 1 && <p className="mt-1 max-w-xs text-xs text-muted">Optional shortcut. Keeps dates you edited individually.</p>}
      </div>
      <p className="text-xs text-muted">LFD/ERD, cutoff, and mileage are filled from the PRO. Changes here apply only to this request.</p>
      <div className="overflow-x-auto rounded-lg border border-theme-border-primary">
        <table className="w-full text-left text-xs" aria-label="PROs in this request">
          <thead className="border-b border-theme-border-primary bg-gray-50 text-heading dark:bg-gray-800"><tr className="align-top">
            <th scope="col" className="px-2 py-2">Include</th>
            <th scope="col" className="min-w-48 px-2 py-2">PRO / customer</th>
            <th scope="col" className="min-w-28 px-2 py-2">Market / move</th>
            <th scope="col" className="min-w-44 px-2 py-2">Route / equipment</th>
            <th scope="col" className="min-w-40 px-2 py-2">Live unload / Drop</th>
            <th scope="col" className="min-w-48 px-2 py-2">Requested service date{!form.asap && <span className="text-red-600"> *</span>}</th>
            {showImportDates && <th scope="col" className="min-w-48 px-2 py-2">LFD<span className="block text-xs font-normal text-muted">Last free day · imports</span></th>}
            {showExportDates && <><th scope="col" className="min-w-48 px-2 py-2">ERD<span className="block text-xs font-normal text-muted">Earliest receiving date · exports</span></th><th scope="col" className="min-w-48 px-2 py-2">Cutoff<span className="block text-xs font-normal text-muted">Exports</span></th></>}
            <th scope="col" className="min-w-28 px-2 py-2">Miles<span className="text-red-600"> *</span></th>
          </tr></thead>
          <tbody className="divide-y divide-theme-border-primary bg-panel">{rows.map((row, i) => {
            const blocked = (!!row.appointment && form.for !== 'Bobtail') || requests.some(request => isRequestForPro(request, row.pro) && isOpen(request));
            const disabled = blocked || !row.selected || contextLoading || busy || !!contextFailures[row.pro];
            const update = (values: Partial<typeof row>) => setRows(current => current.map((item, index) => index === i ? { ...item, ...values } : item));
            return <tr key={row.pro} className="align-top">
              <td className="px-2 py-2 text-center"><Toggle size="xs" ariaLabel={`Select PRO ${row.pro}`} checked={row.selected} disabled={blocked || contextLoading || busy} onChange={selected => update({ selected })} /></td>
              <td className="px-2 py-2"><strong className="text-heading">{row.pro}</strong><div className="mt-1 text-body">{row.customer}</div>{row.keyTier && <div className="mt-1 text-xs text-muted">{row.keyTier}</div>}{contextFailures[row.pro] && <p className="mt-1 text-xs text-red-600 dark:text-red-400">Details unavailable</p>}{blocked && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{row.appointment && form.for !== 'Bobtail' ? 'Delivery appointment set' : 'Open request exists'}</p>}</td>
              <td className="px-2 py-2"><div>{row.market || row.dispatcherRegion || 'No dispatcher region'}</div>{!contextLoading && !contextFailures[row.pro] && !marketFromPro(row.market) && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{row.dispatcherRegion ? 'No capacity market configured' : `Dispatcher region missing${row.dispatcher ? ` (${row.dispatcher})` : ''}`}</p>}<div className="mt-1 text-xs text-muted">{row.move || form.move} · {row.pool || form.pool}</div></td>
              <td className="px-2 py-2"><div>{row.pickup || '—'} → {row.delivery || '—'}</div>{form.for !== 'Bobtail' && <div className="mt-1 text-xs text-muted">{row.container || 'No container'}{row.size ? ` · ${row.size}` : ''}</div>}</td>
              <td className="px-2 py-2"><Select label={`Live unload / Drop for ${row.pro}`} value={row.handling || ''} disabled={disabled} options={['', 'Live unload', 'Drop']} onChange={value => update({ handling: value as OrderContext['handling'] })} /></td>
              <td className="px-2 py-2"><CapacityValidationControl name={`Requested date for ${row.pro}`}><CapacityDateInput ariaLabel={`Requested date for ${row.pro}`} disabled={form.asap || disabled} value={row.date} onChange={value => update({ date: value, touched: true })} /></CapacityValidationControl></td>
              {showImportDates && <td className="px-2 py-2">{row.move !== 'EXPORT' && row.move !== 'DOMESTIC' ? <CapacityDateInput ariaLabel={`LFD for ${row.pro}`} disabled={disabled} value={row.lfd || ''} onChange={value => update({ lfd: value })} /> : <span className="text-muted">—</span>}</td>}
              {showExportDates && <><td className="px-2 py-2">{row.move === 'EXPORT' ? <CapacityDateInput ariaLabel={`ERD for ${row.pro}`} disabled={disabled} value={row.erd || ''} onChange={value => update({ erd: value })} /> : <span className="text-muted">—</span>}</td><td className="px-2 py-2">{row.move === 'EXPORT' ? <CapacityDateInput ariaLabel={`Cutoff for ${row.pro}`} disabled={disabled} value={row.cutoff || ''} onChange={value => update({ cutoff: value })} /> : <span className="text-muted">—</span>}</td></>}
              <td className="px-2 py-2"><CapacityValidationControl name={`Mileage for ${row.pro}`}><input aria-label={`Mileage for ${row.pro}`} required={row.selected && !blocked} type="number" min="0" step="any" className={inputClass} disabled={disabled} value={row.miles || ''} onChange={event => update({ miles: event.target.value })} /></CapacityValidationControl></td>
            </tr>;
          })}</tbody>
        </table>
      </div>
    </section></CapacityValidationControl>}
    {!isPro && form.for !== 'Bobtail' && <section aria-label="Loads" className="my-3 space-y-2"><h3 className="text-sm font-semibold text-heading">Loads</h3>
      <div className="rounded-lg border border-theme-border-primary bg-panel p-3">
        <div className="flex flex-wrap items-end gap-3">
          {form.loads.length > 1 && <div className="w-full max-w-xs"><Field label="Set requested date for all loads"><CapacityDateInput ariaLabel="Set requested date for all loads" disabled={form.asap} value={form.date} onChange={value => {
            patch('date', value);
            patch('loads', applySharedDate(form.loads, value));
          }} /></Field></div>}
          <label className="flex items-center gap-2 py-2 text-xs text-body"><Toggle size="xs" ariaLabel="ASAP / on arrival" checked={form.asap} onChange={checked => patch('asap', checked)} />ASAP / on arrival</label>
        </div>
        {form.loads.length > 1 && <p className="mt-1 text-xs text-muted">Optional shortcut. Keeps dates you edited individually.</p>}
      </div>
      {form.loads.map((load, i) => <div key={i} className="rounded-lg border border-theme-border-primary p-3"><div className="mb-1 flex justify-between"><h4 className="text-sm font-semibold">Load {i + 1}</h4><button className={buttonClass} disabled={form.loads.length === 1} onClick={() => patch('loads', form.loads.filter((_, j) => i !== j))}>Remove</button></div><div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, max(220px, calc((100% - 36px) / 4))), 1fr))' }}>{(['origin', 'destination', 'quantity', 'size', 'date', 'miles', ...(form.move === 'IMPORT' ? ['lfd'] : form.move === 'EXPORT' ? ['erd', 'cutoff'] : [])] as const).map(key => {
            const k = key as keyof typeof load;
            const label = ({
              quantity: 'Quantity',
              size: 'Size / type',
              origin: 'Origin',
              destination: 'Destination',
              miles: 'Approximate miles',
              date: 'Requested date',
              lfd: 'LFD',
              erd: 'ERD',
              cutoff: 'Cutoff'
            } as Record<string, string>)[key];
            const update = (v: string) => patch('loads', form.loads.map((l, j) => j === i ? {
              ...l,
              [key]: key === 'quantity' ? Math.max(1, Number(v)) : v,
              touched: key === 'date' || l.touched
            } : l));
            return key === 'origin' && form.move !== 'DOMESTIC' ? <Select key={key} label={label} value={load.origin} options={['', ...(origins[form.market] || []).slice().sort((a, b) => a.localeCompare(b))]} onChange={update} /> : <Field key={key} label={label} validationKey={`${i}:${label}`} required={['quantity', 'destination', 'miles'].includes(key) || key === 'date' && !form.asap}>{['date', 'lfd', 'erd', 'cutoff'].includes(key) ? <CapacityDateInput ariaLabel={label} value={String(load[k])} onChange={update} disabled={key === 'date' && form.asap} /> : <input aria-label={label} required={['quantity', 'destination', 'miles'].includes(key)} step={key === 'miles' ? 'any' : undefined} className={inputClass} value={String(load[k])} type={['quantity', 'miles'].includes(key) ? 'number' : 'text'} min={key === 'quantity' ? 1 : key === 'miles' ? 0 : undefined} disabled={key === 'date' && form.asap} onChange={e => update(e.target.value)} />}</Field>;
          })}</div></div>)}<div className="flex gap-2"><button className={buttonClass} onClick={() => patch('loads', [...form.loads, blankLoad()])}>Add load</button><button className={buttonClass} onClick={() => patch('loads', [...form.loads, {
          ...form.loads[form.loads.length - 1]
        }])}>Duplicate last</button></div></section>}
    <section aria-label="Customer request" className="mt-3 space-y-3 rounded-lg border border-theme-border-primary bg-panel p-3"><h3 className="text-sm font-semibold text-heading">Customer request</h3><Select required label="Reason" value={form.reason} options={isPro ? reasons : preOrderReasons} onChange={v => patch('reason', v)} />{(['ask', 'commitments'] as const).map(k => <Field key={k} required={k === 'ask'} label={k === 'ask' ? 'What is the customer asking for?' : 'What have we told the customer, and what are we committed to?'}><textarea aria-label={k === 'ask' ? 'What is the customer asking for?' : 'What have we told the customer, and what are we committed to?'} required={k === 'ask'} className={inputClass} rows={3} value={form[k]} onChange={e => patch(k, e.target.value)} /></Field>)}

      {isPro && <CapacityVetting checks={ruleChecks} manager={manager} justification={justification} onManagerChange={setManager} onJustificationChange={setJustification} />}
    </section>

    <div className="my-3 space-y-2">
      <Field label="Documentation (up to 4 files, 5 MB each)"><input type="file" multiple className={inputClass} onChange={e => setFiles(Array.from(e.target.files ?? []))} /></Field>
      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  </div></CapacityValidationContext.Provider>;
  return (
    <CapacityRequestDrawer isOpen onClose={onClose}
      title={isPro ? `Request Capacity — ${orders.length === 1 ? `PRO ${orders[0].pro}` : `${orders.length} PROs`}` : 'Request Capacity — Pre-order'}
      initialWidth={isPro ? 1100 : 900} contentClassName="relative flex-1 overflow-y-auto px-4 py-3" headerClassName="px-4 py-2" footer={footer}>
      {content}
    </CapacityRequestDrawer>
  );
}
